import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { Link } from 'react-router-dom';
import DataTable, { createTheme } from 'react-data-table-component';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../services/api';

import ViewProjectModal from './ViewProjectModal';
import EditProjectModal from './EditProjectModal';
import DeleteConfirmationModal from '../DeleteConfirmationModal';

// 1. Create a custom theme (optional)
createTheme('customTheme', {
  // ... (your theme settings - kept for brevity)
  text: {
    primary: '#212529',
    secondary: '#495057',
  },
  background: {
    default: '#ffffff',
  },
  context: {
    background: '#e3f2fd',
    text: '#212529',
  },
  divider: {
    default: '#dee2e6',
  },
  action: {
    button: '#fff',
    hover: 'rgba(0,0,0,0.08)',
    disabled: 'rgba(0,0,0,0.12)',
  },
});

// 2. Custom styles
const customStyles = {
  // ... (your custom styles - kept for brevity)
  rows: {
    style: {
      minHeight: '50px', // row height
      fontSize: '15px',
    },
  },
  headCells: {
    style: {
      backgroundColor: '#f8f9fa',
      fontWeight: '600',
      fontSize: '1rem',
    },
  },
  cells: {
    style: {
      fontSize: '0.95rem',
    },
  },
};


const ProjectsTable = () => {
  const [projects, setProjects] = useState([]);
  const [userType, setUserType] = useState(null);

  // For modals
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);

  const [currentProject, setCurrentProject] = useState(null);
  const [editedProject, setEditedProject] = useState(null);
  const [deletedProject, setDeletedProject] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [launchFilter, setLaunchFilter] = useState('');
  const [sponsoredFilter, setSponsoredFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState(''); // <-- Step 1: Add state for search term

  // Fetch projects/user on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUserType(parsedUser.type);
      fetchAllProjects(parsedUser.type);
    }
  }, []);

  // Refresh projects after modals close
  const refreshProjects = () => {
    if (userType) {
      fetchAllProjects(userType);
    }
  };

  const fetchAllProjects = async (type) => {
    try {
      const endpoint = type === 'admin'
        ? '/projects/unpaginated'
        : '/projects/developer/projects';
      const { data } = await api.get(endpoint);

      if (data.status) {
        // Ensure data is an array
        const projectData = Array.isArray(data.response.data) ? data.response.data : [];
        setProjects(projectData);
      } else {
        setProjects([]);
        console.error('Error fetching projects:', data.message);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]); // Ensure projects is an array even on error
    }
  };


  // ----- View -----
  const handleView = (project) => {
    setCurrentProject(project);
    setShowViewModal(true);
  };
  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setCurrentProject(null);
    refreshProjects();
  };

  // ----- Edit -----
  const handleEdit = (project) => {
    setEditedProject(project);
    setShowEditModal(true);
  };
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditedProject(null);
    refreshProjects();
  };

  // ----- Delete -----
  const handleDeleteProject = (project) => {
    setDeletedProject(project);
    setShowDeleteConfirmationModal(true);
  };
  const handleCloseDeleteModal = () => {
    setShowDeleteConfirmationModal(false);
    setDeletedProject(null);
    refreshProjects();
  };

  const handleDelete = async () => {
    if (!deletedProject) return; // Guard clause
    try {
      await api.delete(`/projects/${deletedProject.id}`);
      if (userType === 'admin') {
        toast.success('Project Deleted Successfully!');
      } else {
        toast.success('Deletion request is submitted! Wait for approval.');
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      const errorMessage = error.response?.data?.message || error.message || 'An unknown error occurred';
      toast.error(`Error deleting project: ${errorMessage}`);
    } finally {
      handleCloseDeleteModal(); // Refresh happens inside this now
    }
  };


  // ----- Filters -----
  const handleStatusFilterChange = (e) => setStatusFilter(e.target.value);
  const handleLaunchFilterChange = (e) => setLaunchFilter(e.target.value);
  const handleSponsoredFilterChange = (e) => setSponsoredFilter(e.target.value);
  // <-- Step 3: Handle search input changes -->
  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  // Apply filters and search
  const filteredProjects = projects.filter((p) => {
    // Type guard to ensure p and its properties exist
    if (!p || typeof p !== 'object') return false;

    // Existing Filters
    const matchStatus = statusFilter ? p.status === statusFilter : true;
    const matchLaunch = launchFilter
      ? p.isLaunch === (launchFilter === 'yes' ? 1 : 0)
      : true;
    const matchSponsored = sponsoredFilter
      ? p.isSponsored === (sponsoredFilter === 'yes' ? 1 : 0)
      : true;

    // <-- Step 4: Add search term filtering -->
    const searchLower = searchTerm.toLowerCase();
    const matchSearch = searchTerm
      ? (p.id && String(p.id).toLowerCase().includes(searchLower)) || // Check ID
      (p.name && p.name.toLowerCase().includes(searchLower)) ||     // Check Name
      (p.status && p.status.toLowerCase().includes(searchLower)) ||
      (p.status && p.developer.name.toLowerCase().includes(searchLower))    // Check Status (optional)
      : true; // If no search term, it's a match

    return matchStatus && matchLaunch && matchSponsored && matchSearch; // Combine all
  });


  // ----- Table Columns -----
  // No changes needed in columns definition
  const columns = [
    {
      name: 'Project ID',
      selector: (row) => row.id,
      sortable: true,
      wrap: true,
    },
    {
      name: 'Developer',
      selector: (row) => row.developer.name,
      sortable: true,
      wrap: true,
    },
    {
      name: 'Name',
      selector: (row) => row.name,
      sortable: true,
      wrap: true,
    },


    {
      name: 'Launch',
      selector: (row) => (row.isLaunch ? 'Yes' : 'No'),
      sortable: true,
      cell: (row) => (
        <span
          className={`badge rounded-pill px-3 py-2 bg-${row.isLaunch ? 'success' : 'warning'
            } text-white`}
        >
          {row.isLaunch ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      name: 'Sponsored',
      selector: (row) => (row.isSponsored ? 'Yes' : 'No'),
      sortable: true,
      cell: (row) => (
        <span
          className={`badge rounded-pill px-3 py-2 bg-${row.isSponsored ? 'success' : 'warning'
            } text-white`}
        >
          {row.isSponsored ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      name: 'Status',
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => {
        let colorClass = 'warning';
        let statusLabel = 'Pending'; // Default

        // Handle potential null/undefined status
        const currentStatus = row.status ? row.status.toLowerCase() : 'pending';

        if (currentStatus === 'approved') {
          colorClass = 'success';
          statusLabel = 'Approved';
        } else if (currentStatus === 'rejected' || currentStatus === 'declined') { // Accept both 'rejected' and 'declined'
          colorClass = 'danger';
          statusLabel = 'Rejected'; // Or 'Declined' if you prefer
        } else {
          // Explicitly handle 'pending' or any other status
          colorClass = 'warning';
          statusLabel = 'Pending';
        }

        return (
          <span className={`badge rounded-pill px-3 py-2 bg-${colorClass} text-white`}>
            {statusLabel}
          </span>
        );
      },
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div>
          {/* View Button */}
          <button
            onClick={() => handleView(row)}
            className="w-32-px h-32-px me-1 me-md-2 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center border-0" // Added border-0 for consistency
            title="View Details" // Added title for accessibility
          >
            <Icon icon="iconamoon:eye-light" />
          </button>
          {/* Edit Button */}
          <button
            onClick={() => handleEdit(row)}
            className="w-32-px h-32-px me-1 me-md-2 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
            title="Edit Project"
          >
            <Icon icon="lucide:edit" />
          </button>
          {/* Delete Button */}
          <button
            onClick={() => handleDeleteProject(row)}
            className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
            title="Delete Project"
          >
            <Icon icon="mingcute:delete-2-line" />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '130px', // Optional: Adjust width if buttons wrap undesirably
    },
  ];


  return (
    <div className="card basic-data-table p-3">
      {/* Filters Row */}
      <div className="row mb-3 gy-3 align-items-end"> {/* Added gy-3 for vertical spacing on smaller screens */}
        {/* Search Bar Input - Step 2 */}
        <div className="col-md-3">
          <label htmlFor="projectSearch" className="form-label fw-bold">Search</label>
          <input
            id="projectSearch" // Added id for label association
            type="text"
            className="form-control"
            placeholder="Search by ID, Name, Status..."
            value={searchTerm}
            onChange={handleSearchChange} // Use the handler
          />
        </div>

        {/* Status Filter */}
        <div className="col-md-2"> {/* Adjusted column size */}
          <label htmlFor="statusFilter" className="form-label fw-bold">Status</label>
          <select
            id="statusFilter"
            className="form-select"
            value={statusFilter}
            onChange={handleStatusFilterChange}
          >
            <option value="">All</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option> {/* Changed from declined to rejected based on your code */}
          </select>
        </div>
        {/* Launch Filter */}
        <div className="col-md-2"> {/* Adjusted column size */}
          <label htmlFor="launchFilter" className="form-label fw-bold">Launch</label>
          <select
            id="launchFilter"
            className="form-select"
            value={launchFilter}
            onChange={handleLaunchFilterChange}
          >
            <option value="">All</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        {/* Sponsored Filter */}
        <div className="col-md-2"> {/* Adjusted column size */}
          <label htmlFor="sponsoredFilter" className="form-label fw-bold">Sponsored</label>
          <select
            id="sponsoredFilter"
            className="form-select"
            value={sponsoredFilter}
            onChange={handleSponsoredFilterChange}
          >
            <option value="">All</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        {/* Add New Button */}
        <div className="col-md-3 text-md-end"> {/* Adjusted column size and alignment */}
          <Link
            to="/add-project"
            className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2 w-100 w-md-auto" // Adjusted width for responsiveness
          >
            <Icon icon="ic:baseline-plus" className="icon fs-6" /> {/* Used fs-6 instead of text-sm */}
            Add New Project
          </Link>
        </div>
      </div>

      {/* DataTable */}
      <div className="table-responsive"> {/* Optional: Wrap table for better responsive behavior */}
        <DataTable
          columns={columns}
          data={filteredProjects}
          theme="customTheme"
          customStyles={customStyles}
          pagination
          responsive // Keep this, but table-responsive wrapper helps too
          highlightOnHover
          striped
          noDataComponent={<div className="p-4 text-center">No results found matching your criteria.</div>} // Enhanced no data message
          paginationComponentOptions={{
            rowsPerPageText: 'Rows per page:',
            rangeSeparatorText: 'of',
          }} // Optional: Customize pagination text
        />
      </div>


      {/* View Modal */}
      {showViewModal && currentProject && ( // Added check for currentProject
        <ViewProjectModal
          show={showViewModal}
          handleClose={handleCloseViewModal}
          project={currentProject}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && editedProject && ( // Added check for editedProject
        <EditProjectModal
          show={showEditModal}
          handleClose={handleCloseEditModal}
          project={editedProject}
        />
      )}

      {/* Delete Confirmation Modal */}
      {/* Keep existing DeleteConfirmationModal logic, but ensure it handles the state correctly */}
      <DeleteConfirmationModal
        show={showDeleteConfirmationModal}
        handleClose={handleCloseDeleteModal}
        handleDelete={handleDelete}
        // Pass necessary props or use children as before
        itemType="Project" // Example of passing item type for generic modal
        itemName={deletedProject ? deletedProject.name : ''} // Example: display name
      >
        {/* You can customize the content passed to DeleteConfirmationModal */}
        {/* Or define the content structure inside the DeleteConfirmationModal itself */}
        <Modal.Header closeButton>
          <Modal.Title>Delete Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the project "{deletedProject?.name || 'this project'}"?
          {userType !== 'admin' && <p className="text-warning mt-2 small">Note: As a developer, this will submit a deletion request for admin approval.</p>}
        </Modal.Body>
      </DeleteConfirmationModal>
    </div>
  );
};

export default ProjectsTable;